import { useAuthContext } from './AuthContext';
import { Suspense, lazy, ReactNode, Fragment } from 'react';

import { BrowserRouter, Routes, Route, Navigate} from'react-router';
import { SandboxBanner } from '../components/SandboxBanner';
import { Tutorial } from '../components/Tutorial';
import { designTokens } from '../theme/theme';

const Account = lazy(() => import("../pages/client/Account"));
const Alumni = lazy(() => import("../pages/client/Alumni"));
const Certificate = lazy(() => import("../pages/client/Certificate"));
const AlumniSurvey = lazy(() => import("../pages/client/AlumniSurvey"));
const Announcement = lazy(() => import('../pages/admin/Announcement'));
const Discussion = lazy(() => import('../pages/client/Discussion'));
const Error = lazy(() => import('../pages/admin/Error'));
const Event = lazy(() => import('../pages/admin/Event'));
const Facilitator = lazy(() => import('../pages/client/Facilitator'));
const Guideline = lazy(() => import('../pages/client/Guideline'));
const Howto = lazy(() => import('../pages/client/Howto'));
const Kickoff = lazy(() => import('../pages/client/Kickoff'));
const LandingDebug = lazy(() => import('../pages/public/LandingDebug'));
const LandingHarvard = lazy(() => import('../pages/public/LandingHarvard'));
const LandingMGH = lazy(() => import('../pages/public/LandingMGH'));
const Login = lazy(() => import('../pages/public/Login'));
const Logout = lazy(() => import('../pages/public/Logout'));
const MakeUp = lazy(() => import('../pages/client/MakeUp'));
const MakeUpCheckIn = lazy(() => import('../pages/client/MakeUpCheckIn'));
const MakeUpCheckInCaseStudy = lazy(() => import('../pages/client/MakeUpCheckInCaseStudy'));
const Matching = lazy(() => import('../pages/admin/Matching'));
const MatchingSurvey = lazy(() => import('../pages/client/MatchingSurvey'));
const Payment = lazy(() => import('../pages/client/Payment'));
const Overview = lazy(() => import('../pages/client/Overview'));
const PostMeeting = lazy(() => import('../pages/client/PostMeeting'));
const PostProgram = lazy(() => import('../pages/client/PostProgram'));
const ProgramEval = lazy(() => import('../pages/client/ProgramEval'));
const Reflection = lazy(() => import('../pages/client/Reflection'));
const Report = lazy(() => import('../pages/admin/Report'));
const Resource = lazy(() => import('../pages/client/Resource'));
const ResetPassword = lazy(() => import('../pages/public/ResetPassword'));
const Settings = lazy(() => import('../pages/admin/Settings'));
const Signup = lazy(() => import('../pages/public/Signup'));
const Survey = lazy(() => import('../pages/admin/Survey'));
const User = lazy(() => import('../pages/admin/User'));

const Admin = lazy(() => import('../pages/admin/Admin'));
const Home = lazy(() => import('../pages/client/Home'));

const AdminBar = lazy(() => import('../pages/admin/AdminBar'));
const UserBar = lazy(() => import('../pages/client/UserBar'));
const Loader = lazy(() => import('../components/Loader'));

interface ProtectedRouteProps {
  element: ReactNode;
  allowedRoles: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ element, allowedRoles }) => {
  const { user, loading, isTutorial, setIsTutorial, viewMode } = useAuthContext();
  
  if (loading) {
    return <Loader />;
  }
  
  if (!loading && !user) {
    return <Navigate to="/login" />;
  }
  
  if (user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" />;
  }

  return (
    <Fragment>
      {viewMode === 'admin' || viewMode === 'facilitator' ? <AdminBar /> : <UserBar />}
      {user?.current_group === 'facilitators' && user?.confirm &&
        <SandboxBanner key='sandbox-banner' startTutorial={() => setIsTutorial(true)} role={user?.role} />
      }
      {user?.current_group === 'facilitators'
        && user?.role !== 'peer'
        && user?.confirm
        &&
        <>
          <Tutorial key='tutorial'
            show={isTutorial} 
            onClose={() => setIsTutorial(false)} 
          />
        </>
      }
      {element}
    </Fragment>
  );
};
  

export default function ConvuRouter() {
  const {user, viewMode} = useAuthContext();
  const allRoles = ['admin', 'facilitator', 'peer', 'editor'];
  const facilitatorRoles = ['admin', 'facilitator', 'editor'];

  return (
    <Suspense fallback={<Loader />}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/mgh" element={<LandingMGH />} />
          <Route path="/harvard" element={<LandingHarvard />} />
          <Route path="/debug" element={<LandingDebug />} />
          <Route path="/" element={<ProtectedRoute element={(viewMode === 'peer') ? <Home /> : <Admin />} allowedRoles={allRoles} />} />
          <Route path="/account" element={<ProtectedRoute element={<Account />} allowedRoles={allRoles} />} />
          <Route path="/certificate" element={<ProtectedRoute element={<Certificate />} allowedRoles={allRoles} />} />
          <Route path="/alumni" element={<ProtectedRoute element={<Alumni />} allowedRoles={allRoles} />} />
          <Route path="/alumni-survey" element={<ProtectedRoute element={<AlumniSurvey />} allowedRoles={allRoles} />} />
          <Route path="/discussion" element={<ProtectedRoute element={<Discussion />} allowedRoles={allRoles} />} />
          <Route path="/facilitators" element={<ProtectedRoute element={<Facilitator />} allowedRoles={allRoles} />} />
          <Route path="/guidelines" element={<ProtectedRoute element={<Guideline />} allowedRoles={allRoles} />} />
          <Route path="/howto" element={<ProtectedRoute element={<Howto />} allowedRoles={allRoles} />} />
          <Route path="/kickoff" element={<ProtectedRoute element={<Kickoff />} allowedRoles={allRoles} />} />
          <Route path="/makeup" element={<ProtectedRoute element={<MakeUp />} allowedRoles={allRoles} />} />
          <Route path="/checkin-makeup" element={<ProtectedRoute element={<MakeUpCheckIn />} allowedRoles={allRoles} />} />
          <Route path="/checkin-case-study-makeup" element={<ProtectedRoute element={<MakeUpCheckInCaseStudy />} allowedRoles={allRoles} />} />
          <Route path="/matching-survey" element={<ProtectedRoute element={<MatchingSurvey />} allowedRoles={allRoles} />} />
          <Route path="/payment" element={<ProtectedRoute element={<Payment />} allowedRoles={allRoles} />} />
          <Route path="/overview" element={<ProtectedRoute element={<Overview />} allowedRoles={allRoles} />} />
          <Route path="/post-meeting" element={<ProtectedRoute element={<PostMeeting />} allowedRoles={allRoles} />} />
          <Route path="/post-program" element={<ProtectedRoute element={<PostProgram />} allowedRoles={allRoles} />} />
          <Route path="/program-eval" element={<ProtectedRoute element={<ProgramEval />} allowedRoles={allRoles} />} />
          <Route path="/reflection" element={<ProtectedRoute element={<Reflection />} allowedRoles={allRoles} />} />
          <Route path="/resources" element={<ProtectedRoute element={<Resource />} allowedRoles={allRoles} />} />

          <Route path="/announcement" element={<ProtectedRoute element={<Announcement />} allowedRoles={facilitatorRoles} />} />
          <Route path="/event" element={<ProtectedRoute element={<Event />} allowedRoles={facilitatorRoles} />} />
          <Route path="/matching" element={<ProtectedRoute element={<Matching />} allowedRoles={facilitatorRoles} />} />
          <Route path="/report" element={<ProtectedRoute element={<Report />} allowedRoles={facilitatorRoles} />} />
          <Route path="/settings" element={<ProtectedRoute element={<Settings />} allowedRoles={facilitatorRoles} />} />
          <Route path="/survey" element={<ProtectedRoute element={<Survey />} allowedRoles={facilitatorRoles} />} />
          <Route path="/user" element={<ProtectedRoute element={<User />} allowedRoles={facilitatorRoles} />} />

          <Route path="*" element={<Error />} />
        </Routes>
      </BrowserRouter>
    </Suspense>
  );
};